// src/styles/ExercisesStyles.js
import styled, { keyframes } from 'styled-components';
import { Card, StyledButton } from '../main/CommonStyles';

export const ExerciseDuration = styled.span`
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.secondaryDark};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
`;

export const ExercisesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: ${({ theme }) => theme.spacing.lg};
    margin-top: ${({ theme }) => theme.spacing.lg};

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: ${({ theme }) => theme.spacing.md};
    }
`;

export const ExerciseIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: ${({ theme, $color }) => $color || theme.colors.primaryLightest};
    color: ${({ theme, $iconColor }) => $iconColor || theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.md};
    transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color 0.3s ease;
    flex-shrink: 0;
    margin-bottom: ${({ theme }) => theme.spacing.md};

    svg {
        width: 40px;
        height: 40px;
    }

    @media (max-width: 768px) {
        width: 60px;
        height: 60px;
        svg {
            width: 32px;
            height: 32px;
        }
        margin-right: ${({ theme }) => theme.spacing.md};
        margin-bottom: 0;
    }
`;

export const ExerciseCardStyled = styled(Card)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.3s ease, border-color 0.3s ease;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    position: relative;
    overflow: hidden;
    padding: ${({ theme }) => theme.spacing.lg};
    background: linear-gradient(145deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.neutralLight});
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-radius: ${({ theme }) => theme.borderRadius};
    z-index: 1;

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 5px;
        background: ${({ theme }) => theme.colors.primary};
        border-radius: ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius} 0 0;
        transition: background 0.3s ease;
    }

    .exercise-image-background {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 0;
        opacity: 0.05;
        filter: grayscale(100%) blur(2px);
        transition: opacity 0.4s ease, transform 0.4s ease, filter 0.4s ease;
    }

    & > * {
        position: relative;
        z-index: 1;
    }

    .completed-badge {
        position: absolute;
        top: ${({ theme }) => theme.spacing.sm};
        right: ${({ theme }) => theme.spacing.sm};
        color: ${({ theme }) => theme.colors.secondaryDark};
        background-color: ${({ theme }) => theme.colors.secondaryLight + '40'};
        border-radius: 50%;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: 700;
        z-index: 2;
        svg {
            width: 18px;
            height: 18px;
        }
    }

    &:hover .exercise-image-background {
        opacity: 0.12;
        transform: scale(1.15);
        filter: grayscale(50%) blur(1px);
    }

    &.completed {
        border: 2px solid ${({ theme }) => theme.colors.secondary};
        background: linear-gradient(145deg, ${({ theme }) => theme.colors.secondaryLightest + '80'}, ${({ theme }) => theme.colors.neutralLight + '80'});
        box-shadow: ${({ theme }) => theme.shadows.sm};

        &:before {
            background: ${({ theme }) => theme.colors.secondary};
        }
        ${ExerciseIconWrapper} {
            background-color: ${({ theme }) => theme.colors.secondaryLight};
            color: ${({ theme }) => theme.colors.secondaryDark};
            transform: translateY(-5px);
        }
        h3, p {
            color: ${({ theme }) => theme.colors.textMedium};
        }
        .exercise-action-button {
            background-color: ${({ theme }) => theme.colors.secondary};
            border-color: ${({ theme }) => theme.colors.secondary};
            color: ${({ theme }) => theme.colors.white};
            box-shadow: ${({ theme }) => theme.shadows.sm};
            &:hover:not(:disabled) {
                background-color: ${({ theme }) => theme.colors.secondaryDark};
                border-color: ${({ theme }) => theme.colors.secondaryDark};
                box-shadow: ${({ theme }) => theme.shadows.md};
            }
        }
    }

    &:hover:not(.completed) {
        transform: translateY(-10px) scale(1.04);
        box-shadow: ${({ theme }) => theme.shadows.xl};
        border-color: ${({ theme }) => theme.colors.primary};
        &:before {
            background: linear-gradient(90deg, ${({ theme }) => theme.colors.primaryLight}, ${({ theme }) => theme.colors.primary});
        }
        ${ExerciseIconWrapper} {
            transform: scale(1.2) translateY(-5px);
            background-color: ${({ theme }) => theme.colors.primaryDark};
            color: ${({ theme }) => theme.colors.white};
        }
    }
    &:hover.completed {
        box-shadow: ${({ theme }) => theme.shadows.lg};
        border-color: ${({ theme }) => theme.colors.secondaryDark};
        ${ExerciseIconWrapper} {
            transform: scale(1.05) translateY(-5px);
        }
    }

    .exercise-card-content {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        width: 100%;
    }

    h3 {
        font-size: 1.45rem;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.primaryDarker};
        margin-top: ${({ theme }) => theme.spacing.sm};
        margin-bottom: ${({ theme }) => theme.spacing.xs};
        transition: color 0.3s ease;
        line-height: 1.2;
    }

    p {
        font-size: 0.95rem;
        color: ${({ theme }) => theme.colors.textMedium};
        margin-bottom: ${({ theme }) => theme.spacing.md};
        line-height: 1.6;
        flex-grow: 1;
        min-height: 70px;
    }

    .exercise-action-button {
        width: 80%;
        margin-top: auto;
        margin-left: auto;
        margin-right: auto;
        font-size: 0.95rem;
        padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${({ theme }) => theme.spacing.sm};
        font-weight: 600;
        border-radius: ${({ theme }) => theme.buttonBorderRadius};
        box-shadow: ${({ theme }) => theme.shadows.sm};
        transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s, border-color 0.2s;
        background-color: ${({ theme }) => theme.colors.primary};
        color: ${({ theme }) => theme.colors.white};
        border: 2px solid transparent;

        &:hover:not(:disabled) {
            background-color: ${({ theme }) => theme.colors.primaryDark};
            box-shadow: ${({ theme }) => theme.shadows.md};
            transform: translateY(-2px);
        }
        &:active:not(:disabled) {
            transform: scale(0.96) translateY(0px);
        }
        &:disabled {
            background-color: ${({ theme }) => theme.colors.neutralMedium};
            color: ${({ theme }) => theme.colors.textDisabled};
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
        }
    }


    @media (max-width: 768px) {
        flex-direction: row;
        text-align: left;
        align-items: flex-start;
        padding: ${({ theme }) => theme.spacing.md};
        gap: ${({ theme }) => theme.spacing.md};
        box-shadow: ${({ theme }) => theme.shadows.sm};
        background: ${({ theme }) => theme.colors.white};
        border: 1px solid ${({ theme }) => theme.colors.borderColor};

        &:before {
            display: none;
        }

        .exercise-image-background {
            opacity: 0.03;
            filter: grayscale(100%) blur(0.5px);
        }

        .completed-badge {
            top: ${({ theme }) => theme.spacing.xs};
            right: ${({ theme }) => theme.spacing.xs};
            padding: 3px;
            svg {
                width: 16px;
                height: 16px;
            }
        }

        &:hover:not(.completed) {
            transform: translateY(-3px);
            box-shadow: ${({ theme }) => theme.shadows.md};
            border-color: ${({ theme }) => theme.colors.primaryLight};
            ${ExerciseIconWrapper} {
                transform: none;
            }
        }
        &:hover.completed {
            box-shadow: ${({ theme }) => theme.shadows.md};
            border-color: ${({ theme }) => theme.colors.secondaryLight};
        }

        .exercise-card-content {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            justify-content: flex-start;
            min-height: auto;
        }
        h3 {
            font-size: 1.1rem;
            margin-top: 0;
            margin-bottom: ${({ theme }) => theme.spacing.xs};
        }
        p {
            font-size: 0.8rem;
            min-height: 2.8em;
            max-height: 2.8em;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            margin-bottom: ${({ theme }) => theme.spacing.xs};
        }
        ${ExerciseDuration} {
            font-size: 0.65rem;
            margin-bottom: 0;
            color: ${({ theme }) => theme.colors.textMedium};
            text-align: left;
            width: auto;
            order: -1;
            margin-bottom: ${({ theme }) => theme.spacing.xs};
        }

        .exercise-action-button {
            width: 100%;
            font-size: 0.75rem;
            padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
            min-height: 36px;
            margin-top: ${({ theme }) => theme.spacing.sm};
            align-self: flex-start;
            order: 1;
        }
    }
`;

export const NoExercisesMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.lg};
    color: ${({ theme }) => theme.colors.textDisabled};
    background-color: ${({ theme }) => theme.colors.neutralLight};
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-top: ${({ theme }) => theme.spacing.lg};
    font-size: 1.1rem;
`;

export const ImmersiveExercisePlayer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    position: relative; // Pozycja relative dla desktopu
    overflow: hidden;
    color: ${({ theme }) => theme.colors.textDark};
    background: ${({ theme }) => `linear-gradient(170deg, ${theme.colors.primaryLightest} 0%, ${theme.colors.white} 40%, ${theme.colors.secondaryLight + '40'} 100%)`};

    // Tło z obrazkiem
    &:before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background-image: ${({ imageUrl }) => `url(${imageUrl})`};
        background-size: cover;
        background-position: center;
        opacity: 0.1;
        filter: blur(10px) saturate(1.2);
        z-index: 0;
    }

    // Wszystkie dzieci muszą być nad tłem
    & > * {
        z-index: 1;
        position: relative;
    }

    // Zmiany dla mobile - pełny ekran
    @media (max-width: 768px) {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 2000; // Wyżej niż menu hamburger
        border-radius: 0;
    }
`;

export const PlayerTopBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;
    background-color: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};

    .exercise-title-active {
        font-size: 1.1rem;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.primaryDarker};
        text-align: center;
        flex-grow: 1;
        margin: 0 ${({ theme }) => theme.spacing.sm};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const ProgressIndicator = styled.div`
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.textMedium};
    font-weight: 500;
    min-width: 80px;
    text-align: right;
`;

export const MainStepArea = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};
    text-align: center;
    overflow-y: auto;
    min-height: 0;
`;

export const StepInstruction = styled.p`
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.textDark};
    text-shadow: 0 1px 3px rgba(0,0,0,0.1);
    margin-bottom: ${({ theme }) => theme.spacing.md};
    max-width: 90%;

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

export const StepNote = styled.p`
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.textMedium};
    background-color: rgba(255, 255, 255, 0.7);
    padding: ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-top: ${({ theme }) => theme.spacing.md};
    max-width: 90%;
    line-height: 1.6;
    border-left: 4px solid ${({ theme }) => theme.colors.primary};

    svg {
        margin-right: ${({ theme }) => theme.spacing.xs};
        vertical-align: middle;
        color: ${({ theme }) => theme.colors.primaryDark};
    }
`;

export const PlayerControls = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.lg}; // Większy padding na desktop
    border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
    background-color: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    flex-shrink: 0;
    gap: ${({ theme }) => theme.spacing.lg}; // Większy odstęp
    box-shadow: 0 -5px 15px -5px rgba(0,0,0,0.05); // Cień, by oddzielić od dołu

    button {
        min-width: 140px; // Większe przyciski na desktop
        padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
        font-size: 1rem;
    }

    button[data-variant="success"] {
        background: ${({ theme }) => theme.colors.secondary};
        color: ${({ theme }) => theme.colors.white};
        border-color: ${({ theme }) => theme.colors.secondary};

        &:hover:not(:disabled) {
            background: ${({ theme }) => theme.colors.secondaryDark};
            border-color: ${({ theme }) => theme.colors.secondaryDark};
        }
    }

    @media (max-width: 768px) {
        gap: ${({ theme }) => theme.spacing.sm};
        padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
        box-shadow: none;
        button {
            min-width: 90px;
            font-size: 0.85rem;
            padding: ${({ theme }) => theme.spacing.sm};
            svg {
                width: 18px;
                height: 18px;
            }
        }
    }
`;

export const TimerDisplay = styled.div`
    font-family: ${({ theme }) => theme.fonts.headings};
    font-size: 7rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primaryDark};
    margin: ${({ theme }) => theme.spacing.lg} 0;
    text-shadow: 0 2px 5px rgba(0,0,0,0.1);

    @media (max-width: 768px) {
        font-size: 4.5rem;
        margin: ${({ theme }) => theme.spacing.md} 0;
    }
`;

const breatheAnimationInhale = keyframes`
    0% { transform: scale(0.9); opacity: 0.8; }
    100% { transform: scale(1.2); opacity: 1; }
`;
const breatheAnimationExhale = keyframes`
    0% { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(0.9); opacity: 0.8; }
`;
const breatheAnimationHold = keyframes`
    0%, 100% { transform: scale(1.2); opacity: 1; } // Trzymaj na "wdechu"
`;
const breatheAnimationNormal = keyframes`
    0% { transform: scale(1); opacity: 0.7; }
    50% { transform: scale(1.05); opacity: 0.8; }
    100% { transform: scale(1); opacity: 0.7; }
`;

export const BreathingVisualizer = styled.div`
    width: 250px;
    height: 250px;
    margin: ${({ theme }) => theme.spacing.xl} auto;
    border-radius: 50%;
    background: radial-gradient(circle, ${({ theme }) => theme.colors.primaryLight} 0%, ${({ theme }) => theme.colors.primary} 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1.8rem;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.3);
    box-shadow: ${({ theme }) => `0 0 60px ${theme.colors.primaryLight + 'C0'}, ${theme.shadows.xl}`};
    border: 3px solid ${({ theme }) => theme.colors.primaryLightest + '80'};

    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;
    animation-duration: ${props => props.duration || 4}s;

    &.inhale { animation-name: ${breatheAnimationInhale}; animation-iteration-count: 1; }
    &.exhale { animation-name: ${breatheAnimationExhale}; animation-iteration-count: 1; }
    &.hold { animation-name: ${breatheAnimationHold}; animation-iteration-count: 1; }
    &.normal {
        animation-name: ${breatheAnimationNormal};
        animation-iteration-count: infinite;
        animation-duration: 4s;
        background: radial-gradient(circle, ${({ theme }) => theme.colors.primaryLighter} 0%, ${({ theme }) => theme.colors.primaryLight} 100%);
        box-shadow: ${({ theme }) => theme.shadows.lg};
        transform: scale(0.9);
    }

    @media (max-width: 768px) {
        width: 180px;
        height: 180px;
        font-size: 1.3rem;
        margin: ${({ theme }) => theme.spacing.md} auto;
    }
`;

export const HiddenAudioPlayer = styled.audio`
    display: none;
`;